import {
  getLocalStorageData,
  removeLocalStorageData,
} from '@phx-husky/local-storage';
import { graphql, useStaticQuery } from 'gatsby';
import React, {
  createContext,
  FC,
  MouseEvent,
  MouseEventHandler,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { ModalExternalQuery } from '~/components/TMPLinkWrap/models';
import { Language } from '~/hooks/useCurrentLang';
import { gtmService } from '~/services/gtm';
import { GTM_LOCALSTORAGE_DATA } from '~/utils/constants';

import {
  ExternalLinkDialogContextProps,
  ExternalLinkDialogContextType,
} from './models';

export const defaultValue: ExternalLinkDialogContextType = {
  isVisible: false,
  redirectUrl: '',
  isModalExternal: false,
  modalExternal: undefined,
  currentLangCode: null,
  close: () => {
    throw new Error('close not implemented');
  },
  displayModal: () => {
    throw new Error('displayModal not implemented');
  },
  setCurrentLang: () => {
    throw new Error('setCurrentLang not implemented');
  },
};

export const ExternalLinkDialogContext =
  createContext<ExternalLinkDialogContextType>(defaultValue);

const ExternalLinkDialogProvider: FC<
  PropsWithChildren<ExternalLinkDialogContextProps>
> = ({ children, config }) => {
  const [state, setState] =
    useState<ExternalLinkDialogContextType>(defaultValue);

  const {
    allContentstackModalExternal: { nodes: modalsExternal },
  } = useStaticQuery<ModalExternalQuery>(graphql`
    {
      allContentstackModalExternal {
        nodes {
          ...ModalExternalFragment
        }
      }
    }
  `);

  const modalExternal = useMemo(
    () =>
      modalsExternal.find(
        ({ tags, publishDetails: { locale } }) =>
          tags.includes(process.env.GATSBY_CONTENTSTACK_BRAND_TAG as string) &&
          locale === state.currentLangCode
      ),
    [modalsExternal, state.currentLangCode]
  );

  const close: ExternalLinkDialogContextType['close'] = useCallback(() => {
    const lastRetailer = getLocalStorageData(
      GTM_LOCALSTORAGE_DATA.lastRetailerTitle
    );

    if (lastRetailer) {
      gtmService.emitRetailerLinkRejection(lastRetailer, state.redirectUrl);
      removeLocalStorageData(GTM_LOCALSTORAGE_DATA.lastRetailerTitle);
    }

    setState((prevState) => ({ ...prevState, isVisible: false }));
  }, [state.redirectUrl]);

  const isModalExternal = useMemo(
    () =>
      modalExternal?.primaryButtonLabel !== undefined &&
      modalExternal?.secondaryButtonLabel !== undefined &&
      modalExternal?.enable,
    [modalExternal]
  );

  const displayModal: ExternalLinkDialogContextType['displayModal'] =
    useCallback(
      (
        event: MouseEvent<HTMLAnchorElement>,
        url: string,
        onClick?: MouseEventHandler<HTMLAnchorElement>
      ) => {
        if (!isModalExternal) {
          if (onClick) {
            onClick(event);
          }

          return;
        }

        event.preventDefault();
        if (onClick) {
          onClick(event);
        }
        setState((prevState) => ({
          ...prevState,
          isVisible: true,
          redirectUrl: url,
        }));
      },
      [isModalExternal]
    );

  const setCurrentLang: ExternalLinkDialogContextType['setCurrentLang'] =
    useCallback(({ locale }: Language) => {
      setState((prevState) => ({
        ...prevState,
        currentLangCode: locale,
      }));
    }, []);

  const contextValue = useMemo(
    () => ({
      ...state,
      close,
      displayModal,
      setCurrentLang,
      modalExternal,
      isModalExternal,
    }),
    [close, displayModal, isModalExternal, modalExternal, setCurrentLang, state]
  );

  return (
    <ExternalLinkDialogContext.Provider value={config || contextValue}>
      {children}
    </ExternalLinkDialogContext.Provider>
  );
};

export default ExternalLinkDialogProvider;
